var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { useDinPlayerContext } from 'hx-din-test-2';
import { useNavigator } from 'hx-navigator';
import { Fade, Keypad, SideImageLayout, TripletInput, useKeypad } from 'hx-ui';
import { useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { useSetProgress } from 'hx-progress';
import { dinTestActions, selectActiveTestStep } from '@/store';
import { DinTestSidePanel } from '@/components/din-test-side-panel.component';
import { useStyledModal } from '@/hooks/use-styled-modal.hook';
import { KeypadButton } from '@/components/din-test/keypad-button.component';
import { EncouragementNotes, InfoMessage, } from '@/components/din-test/notes.component';
import { StyledProgress } from '@/components/styled-progress.component';
import { cn } from 'hx-util';
import { EargoButton } from '@/components/eargo-button.component';
import { keys } from '@/components/din-test/constants/keys.constant';
import { HelpModalContent, IdleModalContent, } from '@/components/din-test/components/modals/modals.component';
import { useAnalytics } from 'hx-analytics';
import { FaRegQuestionCircle } from 'react-icons/fa';
export function DinTestPage() {
    var theme = useTheme();
    var id = useParams().id;
    var dispatch = useDispatch();
    var _a = useDinPlayerContext(), playTriplet = _a.playTriplet, initialize = _a.initialize, state = _a.state, stop = _a.stop;
    var _b = useState(''), error = _b[0], setError = _b[1];
    var _c = useKeypad(function () { return setError(''); }), value = _c.value, handleChange = _c.handleChange, handleBackspace = _c.handleBackspace, isShaking = _c.isShaking, shake = _c.shake, setValue = _c.setValue, handleClear = _c.handleClear;
    var next = useNavigator({
        phaseLength: 23,
        onComplete: function () {
            dispatch(dinTestActions.processResult());
            dispatch(dinTestActions.sendData());
        },
    }).next;
    var activeStep = useSelector(selectActiveTestStep((id && +id - 1) || 0));
    useSetProgress(1, (id && +id) || 0);
    useMount(function () {
        if (!state.initialized)
            initialize();
        dispatch(dinTestActions.initializeTest());
    });
    useEffect(function () {
        dispatch(dinTestActions.setSnr(id));
    }, [id]);
    var playChallenge = useCallback(function () {
        if ((activeStep === null || activeStep === void 0 ? void 0 : activeStep.snr) === undefined)
            return;
        playTriplet(activeStep.challenge, {
            snr: activeStep.snr,
        });
    }, [activeStep]);
    useEffect(function () {
        playChallenge();
    }, [activeStep]);
    var handleSubmit = useCallback(function () {
        if (value.length < 3) {
            setError('Enter at least 3 numbers, then click OK');
            return shake();
        }
        var response = value;
        dispatch(dinTestActions.submitResponse({ response: response, id: id }));
        setValue([]);
        stop();
        next();
    }, [value, id]);
    var sendButtonClick = useAnalytics().sendButtonClick;
    var _d = useStyledModal(_jsx(IdleModalContent, {})), idleModal = _d[0], toggleIdleModal = _d[1], idleModalOpen = _d[2];
    var _e = useStyledModal(_jsx(HelpModalContent, { sendButtonClick: sendButtonClick })), helpModal = _e[0], toggleHelpModal = _e[1], helpModalOpen = _e[2];
    useIdleTimer({
        onIdle: function () {
            if (idleModalOpen || helpModalOpen)
                return;
            toggleIdleModal();
        },
        timeout: 1000 * 15,
    });
    if (!state.initialized)
        return (_jsx("div", __assign({ className: "grid place-items-center min-h-[31.25rem]" }, { children: _jsx(CircularProgress, {}) })));
    return (_jsx(SideImageLayout, { className: 'overflow-y-auto', leftPanel: _jsx(DinTestSidePanel, {}), header: _jsx(StyledProgress, { className: "px-4 py-6", textClassName: 'font-bold' }), content: _jsx(Fade, __assign({ className: "grid gap-4 place-items-center" }, { children: state.error ? (_jsx(ErrorMessage, { action: playChallenge })) : (_jsxs(_Fragment, { children: [_jsxs(Typography, __assign({ variant: "h2", className: "text-3xl md:text-4xl md:font-extrabold -mt-4" }, { children: ["Step ", id, " of 23"] })), _jsxs("div", __assign({ className: "flex flex-col gap-4" }, { children: [_jsx(TripletInput, { className: "grid-cols-3", value: value, isShaking: isShaking, digitStyle: {
                                    fontSize: '1.875rem',
                                    minHeight: '2em',
                                    borderBottomWidth: '3px',
                                    paddingLeft: '10px',
                                    paddingRight: '10px',
                                    borderBottomColor: theme.palette.primary.main,
                                    '&.has-value': {
                                        borderBottomColor: theme.palette.primary.main,
                                    },
                                } }), _jsx(Keypad, { value: value, onChange: handleChange, onBackspace: handleBackspace, onSubmit: handleSubmit, KeypadButton: KeypadButton, onClear: handleClear, keys: keys })] })), _jsx(InfoMessage, { id: id }), _jsx(Typography, __assign({ className: cn('text-eargo-caribbean-current font-bold pb-4 -mt-3 inline-block h-7', {
                            'text-eargo-red': Boolean(error),
                        }) }, { children: Boolean(error) ? _jsx(_Fragment, { children: error }) : _jsx(EncouragementNotes, { id: id }) })), _jsx("button", __assign({ onClick: function () { return toggleHelpModal(); }, className: 'md:hidden w-full flex justify-end ' }, { children: _jsx(FaRegQuestionCircle, { color: "black", className: 'w-6 h-6' }) })), idleModal, helpModal] })) })) }));
}
var ErrorMessage = function (_a) {
    var action = _a.action;
    return (_jsxs("div", __assign({ className: "grid gap-4 place-items-center" }, { children: [_jsx("div", { children: "Oops! An error occurred while trying to play the audio" }), _jsx(EargoButton, __assign({ onClick: action }, { children: "Retry" }))] })));
};
